import { Component, useCallback, useMemo, useState, type ReactNode } from 'react';
import { Bookmark, ChevronDown, ChevronRight, Loader2, Power, Search, Sparkles, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KpiCard } from '@/components/shared/KpiCard';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { SupplyAgentWorkbench } from '@/modules/supply/components/SupplyAgentWorkbench';
import {
  formatSupplyAgentRuleLabel,
  normalizeSupplyAgentRules,
  type SupplyAgentRuleRecord,
} from '@/modules/supply/components/supplyAgentRuleUtils';
import {
  notifySupplyAgentRulesChanged,
  requestSupplyAgentOpen,
} from '@/modules/supply/components/supplyAgentDrawer';
import { supplyAgentService } from '@/services/supplyAgentService';

class SupplyAgentUiBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function ManageableRuleChips({
  rules,
  supplierId,
  warehouseId,
  onChanged,
}: {
  rules: SupplyAgentRuleRecord[];
  supplierId: number | null;
  warehouseId: number;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState<number | null>(null);

  if (rules.length === 0) return null;

  const toggleActive = async (rule: SupplyAgentRuleRecord) => {
    if (rule.id == null || busyId != null) return;
    setBusyId(rule.id);
    try {
      await supplyAgentService.patchRule(rule.id, supplierId, warehouseId, {
        is_active: rule.is_active === false,
      });
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const removeRule = async (rule: SupplyAgentRuleRecord) => {
    if (rule.id == null || busyId != null) return;
    const label = formatSupplyAgentRuleLabel(rule);
    const active = rule.is_active !== false;
    const ok = window.confirm(
      active
        ? t('supply.supplyAgent.deleteRuleConfirm', { label })
        : t('supply.supplyAgent.purgeRuleConfirm', { label }),
    );
    if (!ok) return;
    setBusyId(rule.id);
    try {
      await supplyAgentService.deleteRule(rule.id, supplierId, warehouseId, {
        hard: !active,
      });
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {rules.map((rule, index) => {
        const active = rule.is_active !== false;
        const ruleId = rule.id;
        const busy = ruleId != null && busyId === ruleId;
        return (
          <li
            key={String(ruleId ?? `${rule.rule_type}-${index}`)}
            className={`inline-flex items-center gap-1 rounded-full border pl-2 pr-1 py-0.5 text-[11px] ${
              active
                ? 'border-sky-200 bg-white text-sky-900'
                : 'border-gray-200 bg-gray-50 text-gray-500'
            }`}
            title={formatSupplyAgentRuleLabel(rule)}
          >
            <span className="max-w-[220px] truncate">{formatSupplyAgentRuleLabel(rule)}</span>
            {ruleId != null ? (
              <>
                <button
                  type="button"
                  onClick={() => void toggleActive(rule)}
                  disabled={busy || busyId != null}
                  className="inline-flex items-center justify-center rounded-full p-0.5 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                  title={
                    active
                      ? t('supply.rulesCatalog.deactivate')
                      : t('supply.rulesCatalog.activate')
                  }
                  aria-label={
                    active
                      ? t('supply.rulesCatalog.deactivate')
                      : t('supply.rulesCatalog.activate')
                  }
                >
                  {busy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Power className={`h-3 w-3 ${active ? '' : 'opacity-50'}`} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => void removeRule(rule)}
                  disabled={busy || busyId != null}
                  className="inline-flex items-center justify-center rounded-full p-0.5 text-sky-700 hover:bg-sky-100 hover:text-red-700 disabled:opacity-50"
                  title={t('supply.rulesCatalog.deleteRule')}
                  aria-label={t('supply.rulesCatalog.deleteRule')}
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function SupplyAgentRulesPage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();
  const queryClient = useQueryClient();
  const { isActionEnabled } = useFeatureVisibility();
  const showAgent =
    isActionEnabled('supply.agent_simulate', false) ||
    import.meta.env.VITE_SUPPLY_AGENT_ENABLED === 'true';
  const [query, setQuery] = useState('');
  const [openSupplierId, setOpenSupplierId] = useState<number | null>(null);
  const [agentSupplierId, setAgentSupplierId] = useState<number | null>(null);
  const [agentSupplierName, setAgentSupplierName] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ['supply-agent-rules-catalog', warehouseId, 'manage'],
    queryFn: () =>
      supplyAgentService.rulesCatalog(warehouseId!, { includeInactive: true }),
    enabled: typeof warehouseId === 'number' && warehouseId > 0 && showAgent,
    staleTime: 15_000,
  });

  const catalog = catalogQuery.data;
  const warehouseRules = normalizeSupplyAgentRules(catalog?.warehouse_rules);
  const needle = query.trim().toLowerCase();
  const suppliers = useMemo(() => {
    const rows = catalog?.suppliers || [];
    if (!needle) return rows;
    return rows.filter((row) => {
      if (row.supplier_name.toLowerCase().includes(needle)) return true;
      return normalizeSupplyAgentRules(row.rules).some((rule) =>
        formatSupplyAgentRuleLabel(rule).toLowerCase().includes(needle),
      );
    });
  }, [catalog?.suppliers, needle]);

  const refreshCatalog = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['supply-agent-rules-catalog'] });
    void queryClient.invalidateQueries({ queryKey: ['supply-agent-supplier-rules'] });
    notifySupplyAgentRulesChanged();
  }, [queryClient]);

  const openAgent = (supplierId?: number, supplierName?: string) => {
    setAgentSupplierId(supplierId ?? null);
    setAgentSupplierName(supplierName ?? null);
    requestSupplyAgentOpen();
  };

  if (!showAgent) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-600">{t('supply.rulesCatalog.disabled')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <p className="text-sm text-gray-600">{t('supply.rulesCatalog.manageHint')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard
          title={t('supply.rulesCatalog.kpiRules')}
          value={catalog?.totals.active_rules ?? 0}
          isCount
          icon={<Bookmark className="h-5 w-5 text-sky-600" />}
          loading={catalogQuery.isLoading}
        />
        <KpiCard
          title={t('supply.rulesCatalog.kpiSuppliers')}
          value={catalog?.totals.suppliers_with_rules ?? 0}
          isCount
          loading={catalogQuery.isLoading}
        />
        <KpiCard
          title={t('supply.rulesCatalog.kpiWarehouse')}
          value={catalog?.totals.warehouse_rules ?? 0}
          isCount
          loading={catalogQuery.isLoading}
        />
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('supply.rulesCatalog.searchPlaceholder')}
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
        />
      </div>

      <section className="rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-sky-900">{t('supply.rulesCatalog.warehouseTitle')}</h2>
            <p className="text-xs text-sky-800/80">{t('supply.rulesCatalog.warehouseHint')}</p>
          </div>
          <button
            type="button"
            onClick={() => openAgent()}
            className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t('supply.rulesCatalog.openAgentWarehouse')}
          </button>
        </div>
        {warehouseRules.length === 0 ? (
          <p className="mt-2 text-xs text-sky-800/80">{t('supply.rulesCatalog.warehouseEmpty')}</p>
        ) : typeof warehouseId === 'number' ? (
          <ManageableRuleChips
            rules={warehouseRules}
            supplierId={null}
            warehouseId={warehouseId}
            onChanged={refreshCatalog}
          />
        ) : null}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">{t('supply.rulesCatalog.suppliersTitle')}</h2>
        </div>
        {catalogQuery.isLoading ? (
          <p className="px-4 py-6 text-sm text-gray-500">{t('supply.rulesCatalog.loading')}</p>
        ) : suppliers.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500">
            {needle ? t('supply.rulesCatalog.noSearch') : t('supply.rulesCatalog.suppliersEmpty')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {suppliers.map((row) => {
              const open = openSupplierId === row.supplier_id;
              const rules = normalizeSupplyAgentRules(row.rules);
              return (
                <li key={row.supplier_id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOpenSupplierId(open ? null : row.supplier_id)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      {open ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span className="truncate text-sm font-medium text-gray-900">{row.supplier_name}</span>
                      <span className="shrink-0 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                        {t('supply.rulesCatalog.ruleCount', { count: row.active_count })}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openAgent(row.supplier_id, row.supplier_name)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-sky-800 hover:bg-sky-50"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {t('supply.rulesCatalog.openAgent')}
                    </button>
                  </div>
                  {open && typeof warehouseId === 'number' ? (
                    <ManageableRuleChips
                      rules={rules}
                      supplierId={row.supplier_id}
                      warehouseId={warehouseId}
                      onChanged={refreshCatalog}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {typeof warehouseId === 'number' ? (
        <SupplyAgentUiBoundary>
          <div className="sr-only" aria-live="polite">
            {agentSupplierId == null
              ? t('supply.rulesCatalog.agentContextWarehouse')
              : t('supply.rulesCatalog.agentContextSupplier', {
                  name: agentSupplierName || agentSupplierId,
                })}
          </div>
          <SupplyAgentWorkbench
            supplierId={agentSupplierId}
            warehouseId={warehouseId}
            supplierName={agentSupplierName}
            applyMoqMp
            configOnly
          />
        </SupplyAgentUiBoundary>
      ) : null}
    </div>
  );
}
